import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';

import styles from './share-link-password-form.module.scss';

/** Mirrors the API rules (apps/api/src/share/services/share-link-password.ts). */
export const SHARE_LINK_PASSWORD_MIN_LENGTH = 6;
export const SHARE_LINK_PASSWORD_MAX_LENGTH = 128;

// Not trimmed: spaces are part of the password.
const schema = yup.object().shape({
  password: yup
    .string()
    .required('Enter a password')
    .min(
      SHARE_LINK_PASSWORD_MIN_LENGTH,
      `Use at least ${SHARE_LINK_PASSWORD_MIN_LENGTH} characters`,
    )
    .max(
      SHARE_LINK_PASSWORD_MAX_LENGTH,
      `Use at most ${SHARE_LINK_PASSWORD_MAX_LENGTH} characters`,
    ),
});

type FormValues = { password: string };

type ShareLinkPasswordFormProps = {
  submitLabel: string;
  /** Resolves once saved; a rejection keeps the form open. */
  onSubmit: (password: string) => Promise<void>;
  onCancel?: () => void;
  autoFocus?: boolean;
};

export const ShareLinkPasswordForm: React.FC<ShareLinkPasswordFormProps> = ({
  submitLabel,
  onSubmit,
  onCancel,
  autoFocus,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { password: '' },
    resolver: yupResolver(schema),
  });

  const submit = async ({ password }: FormValues) => {
    try {
      await onSubmit(password);
    } catch {
      // The caller reported the failure; keep the value so the user can retry.
      return;
    }
    // Don't keep the plaintext in form state once it's saved.
    reset({ password: '' });
  };

  return (
    <form className={styles['form']} onSubmit={handleSubmit(submit)} noValidate>
      <Controller
        name="password"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextInput
            name="password"
            label="Password"
            type={isPasswordVisible ? 'text' : 'password'}
            autocomplete="new-password"
            autoFocus={autoFocus}
            value={value}
            onChange={onChange}
            errorMessage={error?.message}
          />
        )}
      />

      <div className={styles['actions']}>
        <TextButton
          type="button"
          text={isPasswordVisible ? 'Hide' : 'Show'}
          onClick={() => setIsPasswordVisible((visible) => !visible)}
        />

        <div className={styles['buttons']}>
          {onCancel && <TextButton type="button" text="Cancel" onClick={onCancel} />}
          <ElevatedButton type="submit" text={submitLabel} isLoading={isSubmitting} />
        </div>
      </div>
    </form>
  );
};
